import { useGoogleMap } from "@react-google-maps/api";
import { useEffect, useRef } from "react";

interface AdvancedMarkerProps {
  position: google.maps.LatLngLiteral;
  title?: string;
  iconUrl?: string;
  iconWidth?: number;
  iconHeight?: number;
  /** "center" pins the image middle on the coordinate. Default is the bottom tip. */
  anchor?: "bottom" | "center";
}

function markerImage(
  iconUrl: string,
  iconWidth: number | undefined,
  iconHeight: number | undefined,
  anchor: "bottom" | "center",
): HTMLImageElement {
  const img = document.createElement("img");
  img.src = iconUrl;
  img.alt = "";

  if (iconWidth) {
    img.width = iconWidth;
    img.style.width = `${iconWidth}px`;
  }

  if (iconHeight) {
    img.height = iconHeight;
    img.style.height = `${iconHeight}px`;
  }

  if (anchor === "center") {
    img.style.transform = "translateY(50%)";
  }

  return img;
}

export function AdvancedMarker({
  position,
  title,
  iconUrl,
  iconWidth,
  iconHeight,
  anchor = "bottom",
}: AdvancedMarkerProps): null {
  const map = useGoogleMap();
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(
    null,
  );
  const positionRef = useRef(position);
  positionRef.current = position;

  useEffect(() => {
    if (!map) {
      return;
    }

    let marker: google.maps.marker.AdvancedMarkerElement | null = null;
    let cancelled = false;

    void (async () => {
      const { AdvancedMarkerElement } = (await google.maps.importLibrary(
        "marker",
      )) as google.maps.MarkerLibrary;

      if (cancelled) {
        return;
      }

      marker = new AdvancedMarkerElement({
        map,
        position: positionRef.current,
        title,
        content: iconUrl
          ? markerImage(iconUrl, iconWidth, iconHeight, anchor)
          : undefined,
      });
      markerRef.current = marker;
    })();

    return () => {
      cancelled = true;
      if (marker) {
        marker.map = null;
      }
      markerRef.current = null;
    };
  }, [map, title, iconUrl, iconWidth, iconHeight, anchor]);

  const { lat, lng } = position;

  useEffect(() => {
    if (!markerRef.current) {
      return;
    }

    markerRef.current.position = { lat, lng };
  }, [lat, lng]);

  return null;
}
